/**
 * Audio Devices Hook
 * Enumerates available microphones and speakers
 */

import { useState, useEffect } from 'react';

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

export interface UseAudioDevicesResult {
  microphones: AudioDevice[];
  speakers: AudioDevice[];
  isLoading: boolean;
  error: string | null;
  refreshDevices: () => Promise<void>;
}

export const useAudioDevices = (): UseAudioDevicesResult => {
  const [microphones, setMicrophones] = useState<AudioDevice[]>([]);
  const [speakers, setSpeakers] = useState<AudioDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const enumerateDevices = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Request microphone permission to get device labels
      // (Without permission, devices show as "id: xxx, label: ''")
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Get all media devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter microphones (audioinput)
      const mics = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          kind: device.kind,
        }));

      // Filter speakers (audiooutput)
      const spkrs = devices
        .filter((device) => device.kind === 'audiooutput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 8)}`,
          kind: device.kind,
        }));

      setMicrophones(mics);
      setSpeakers(spkrs);

      // Stop the temporary stream
      stream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      console.error('Failed to enumerate audio devices:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to access audio devices. Please grant microphone permission.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Enumerate devices on mount
  useEffect(() => {
    enumerateDevices();

    // Listen for device changes (plug/unplug headphones, etc.)
    const handleDeviceChange = () => {
      enumerateDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  return {
    microphones,
    speakers,
    isLoading,
    error,
    refreshDevices: enumerateDevices,
  };
};
